import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AdminTabsComponent, AdminTabOption } from '../ui/admin-tabs.component';
import { AdminUserAccount, AdminUserBusiness, SubscriptionService } from '../../services/subscription.service';
import { AuthService } from '../../services/auth.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { friendlyError } from '../../utils/friendly-error';
import { currentFrontendBaseUrl } from '../../utils/frontend-base-url';

type EditorTab = 'datos' | 'contrasena' | 'acceso';

const ROLE_LABELS: Record<string, string> = {
  customer: 'Cliente',
  owner: 'Propietario',
  employee: 'Sub-administrador',
  super_admin: 'Super Administrador',
};
const RELATION_LABELS: Record<string, string> = {
  owner: 'Propietario registrado',
  delegated_owner: 'Propietario delegado',
  employee: 'Sub-administrador',
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{7,15}$/;
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

/**
 * Super Admin › Usuarios: gestión completa de una cuenta (datos de acceso,
 * contraseña y sesiones). Se usa desde el directorio de usuarios, la ficha
 * del propietario y "Gestionar negocio".
 */
@Component({
  selector: 'app-user-account-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminTabsComponent],
  template: `
    <div class="uae-overlay" (click)="close()">
      <section class="uae-modal" role="dialog" aria-modal="true" aria-labelledby="uae-title" (click)="$event.stopPropagation()">
        <header class="uae-head">
          <span class="uae-avatar" aria-hidden="true">{{ initial() }}</span>
          <div class="uae-identity">
            <span class="uae-eyebrow">GESTIÓN DE CUENTA</span>
            <h3 id="uae-title">{{ user()?.name || 'Cargando cuenta…' }}</h3>
            <div class="uae-badges" *ngIf="user() as u">
              <span class="uae-badge">{{ roleLabel(u.role) }}</span>
              <span class="uae-badge" [class.tone-success]="u.is_active" [class.tone-danger]="!u.is_active">{{ u.is_active ? 'Acceso activo' : 'Acceso desactivado' }}</span>
              <span class="uae-badge tone-warning" *ngIf="u.force_password_change">Debe cambiar su contraseña</span>
            </div>
          </div>
          <button type="button" class="uae-close" (click)="close()" aria-label="Cerrar"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
        </header>

        <div class="uae-tabs"><app-admin-tabs [tabs]="tabs" [active]="tab()" [compact]="true" ariaLabel="Secciones de la cuenta" (tabSelect)="selectTab($event)"></app-admin-tabs></div>

        <div class="uae-body">
          <div class="uae-loading" *ngIf="loading()" role="status"><span class="uae-spinner" aria-hidden="true"></span><span>Cargando la cuenta…</span></div>
          <div class="uae-error" *ngIf="!loading() && loadError()" role="alert"><i class="bi bi-exclamation-octagon" aria-hidden="true"></i><span>{{ loadError() }}</span><button type="button" class="uae-secondary" (click)="load()">Reintentar</button></div>

          <ng-container *ngIf="!loading() && user() as u">
            <!-- DATOS -->
            <form *ngIf="tab() === 'datos'" class="uae-panel" (ngSubmit)="saveProfile()" novalidate>
              <div class="uae-grid">
                <label class="uae-field">
                  <span>Nombre completo</span>
                  <input placeholder="Ej: Ana Pérez" name="uaeName" [(ngModel)]="form.name" maxlength="100" autocomplete="off" required data-no-limit-hint>
                  <small class="uae-field-error" *ngIf="submitted() && nameError()">{{ nameError() }}</small>
                </label>
                <label class="uae-field">
                  <span>Teléfono</span>
                  <input name="uaePhone" [(ngModel)]="form.phone_number" maxlength="20" inputmode="tel" autocomplete="off" placeholder="+503 7000-0000" data-no-limit-hint>
                  <small class="uae-field-error" *ngIf="submitted() && phoneError()">{{ phoneError() }}</small>
                </label>
                <label class="uae-field uae-full">
                  <span>Correo de inicio de sesión</span>
                  <input placeholder="nombre@correo.com" name="uaeEmail" type="email" [(ngModel)]="form.email" maxlength="100" autocomplete="off" required data-no-limit-hint>
                  <small class="uae-hint" *ngIf="!(submitted() && emailError())">Si lo cambias, se avisará al correo anterior y al nuevo.</small>
                  <small class="uae-field-error" *ngIf="submitted() && emailError()">{{ emailError() }}</small>
                </label>
              </div>

              <div class="uae-meta">
                <div><small>Registro</small><strong>{{ formatDate(u.created_at) }}</strong></div>
                <div><small>Último acceso</small><strong>{{ u.last_login_at ? formatDate(u.last_login_at, true) : 'Nunca' }}</strong></div>
                <div><small>ID de usuario</small><strong>#{{ u.user_id }}</strong></div>
              </div>

              <section class="uae-businesses" *ngIf="businesses().length">
                <h4>Negocios vinculados</h4>
                <article *ngFor="let b of businesses()">
                  <span class="uae-business-icon" aria-hidden="true"><i class="bi bi-building"></i></span>
                  <div><strong>{{ b.name }}</strong><small>{{ relationLabel(b.relation) }}{{ b.is_archived ? ' · Dado de baja' : (b.status ? '' : ' · Desactivado') }}</small></div>
                  <button type="button" class="uae-secondary uae-compact" (click)="openBusiness.emit({ id: b.id, name: b.name })"><i class="bi bi-box-arrow-in-up-right" aria-hidden="true"></i> Gestionar</button>
                </article>
              </section>

              <footer class="uae-actions">
                <button type="button" class="uae-secondary" (click)="resetForm()" [disabled]="saving() || !profileDirty()">Descartar</button>
                <button type="submit" class="uae-primary" [disabled]="saving() || !profileDirty()">{{ saving() ? 'Guardando…' : 'Guardar cambios' }}</button>
              </footer>
            </form>

            <!-- CONTRASEÑA -->
            <div *ngIf="tab() === 'contrasena'" class="uae-panel">
              <p class="uae-self-note" *ngIf="isSelf()"><i class="bi bi-info-circle" aria-hidden="true"></i> Esta es tu propia cuenta: cambia tu contraseña desde Configuración. Aquí solo puedes enviarte un enlace de recuperación.</p>

              <article class="uae-card">
                <span class="uae-card-icon" aria-hidden="true"><i class="bi bi-envelope-arrow-up"></i></span>
                <div class="uae-card-copy">
                  <h4>Enviar enlace de recuperación</h4>
                  <p>{{ u.name }} recibirá en <strong>{{ u.email }}</strong> un enlace seguro (válido por 1 hora) para crear su nueva contraseña{{ u.role === 'customer' ? ' en el portal de clientes' : ' en el panel administrativo' }}.</p>
                </div>
                <button type="button" class="uae-secondary" (click)="sendRecoveryLink()" [disabled]="sendingLink()">{{ sendingLink() ? 'Enviando…' : 'Enviar enlace' }}</button>
              </article>

              <article class="uae-card" *ngIf="!isSelf()">
                <span class="uae-card-icon tone-warning" aria-hidden="true"><i class="bi bi-key"></i></span>
                <div class="uae-card-copy">
                  <h4>Asignar una contraseña manualmente</h4>
                  <p>Se reemplaza la contraseña actual y se cierran todas sus sesiones abiertas.</p>
                  <div class="uae-password-row">
                    <div class="uae-password-input">
                      <input [type]="showPassword() ? 'text' : 'password'" name="uaePassword" [(ngModel)]="newPassword" maxlength="200" autocomplete="new-password" placeholder="Mínimo 6 caracteres" aria-label="Nueva contraseña" data-no-limit-hint>
                      <button type="button" (click)="showPassword.set(!showPassword())" [attr.aria-label]="showPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'"><i class="bi" [class.bi-eye]="!showPassword()" [class.bi-eye-slash]="showPassword()" aria-hidden="true"></i></button>
                    </div>
                    <button type="button" class="uae-secondary uae-compact" (click)="generatePassword()"><i class="bi bi-magic" aria-hidden="true"></i> Generar</button>
                    <button type="button" class="uae-secondary uae-compact" (click)="copyPassword()" [disabled]="!newPassword"><i class="bi bi-clipboard" aria-hidden="true"></i> Copiar</button>
                  </div>
                  <small class="uae-field-error" *ngIf="passwordTouched() && newPassword.length < 6">La contraseña debe tener al menos 6 caracteres.</small>
                  <label class="uae-check"><input type="checkbox" name="uaeForce" [(ngModel)]="forceChange"><span>Pedirle que la cambie en su próximo inicio de sesión</span></label>
                  <label class="uae-check"><input type="checkbox" name="uaeNotify" [(ngModel)]="notifyUser"><span>Avisarle por correo que su contraseña fue restablecida (la contraseña no se incluye)</span></label>
                  <div class="uae-card-actions"><button type="button" class="uae-primary" (click)="savePassword()" [disabled]="savingPassword()">{{ savingPassword() ? 'Guardando…' : 'Asignar contraseña' }}</button></div>
                </div>
              </article>
            </div>

            <!-- ACCESO Y SESIONES -->
            <div *ngIf="tab() === 'acceso'" class="uae-panel">
              <article class="uae-card">
                <span class="uae-card-icon" [class.tone-success]="u.is_active" [class.tone-danger]="!u.is_active" aria-hidden="true"><i class="bi" [class.bi-shield-check]="u.is_active" [class.bi-shield-slash]="!u.is_active"></i></span>
                <div class="uae-card-copy">
                  <h4>{{ u.is_active ? 'El acceso está activo' : 'El acceso está desactivado' }}</h4>
                  <p>{{ u.is_active ? 'Puede iniciar sesión normalmente.' : 'No puede iniciar sesión hasta que se reactive su acceso.' }}</p>
                </div>
                <button type="button" [class]="u.is_active ? 'uae-danger' : 'uae-primary'" (click)="toggleStatus()" [disabled]="isSelf() || togglingStatus()">{{ togglingStatus() ? 'Guardando…' : (u.is_active ? 'Desactivar acceso' : 'Activar acceso') }}</button>
              </article>
              <article class="uae-card" *ngIf="!isSelf()">
                <span class="uae-card-icon" aria-hidden="true"><i class="bi bi-phone-flip"></i></span>
                <div class="uae-card-copy">
                  <h4>Cerrar todas las sesiones</h4>
                  <p>Cierra su sesión en todos los dispositivos y navegadores. Deberá volver a iniciar sesión con su contraseña actual.</p>
                </div>
                <button type="button" class="uae-secondary" (click)="revokeSessions()" [disabled]="revoking()">{{ revoking() ? 'Cerrando…' : 'Cerrar sesiones' }}</button>
              </article>
            </div>
          </ng-container>
        </div>
      </section>
    </div>
  `,
  styles: [`
    :host{position:fixed;inset:0;z-index:9700;display:block}
    .uae-overlay{position:absolute;inset:0;display:grid;place-items:center;padding:clamp(10px,3vw,28px);background:var(--ts-bg-navy-950-a76-2,rgba(2,8,20,.72));backdrop-filter:blur(7px)}
    .uae-modal{display:flex;flex-direction:column;box-sizing:border-box;width:min(760px,100%);max-height:calc(100dvh - 24px);overflow:hidden;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:18px;background:linear-gradient(160deg,var(--ts-surface-soft,var(--ts-surface)),var(--ts-bg-elevated));box-shadow:var(--ts-shadow-lg);animation:uaeRise .2s ease-out}
    .uae-head{display:grid;grid-template-columns:48px minmax(0,1fr) 38px;align-items:center;gap:.85rem;padding:1.1rem 1.2rem .8rem}
    .uae-avatar{display:grid;place-items:center;width:48px;height:48px;border-radius:14px;color:var(--ts-on-primary,#fff);background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo));font-size:1.2rem;font-weight:800;text-transform:uppercase}
    .uae-identity{min-width:0}
    .uae-eyebrow{color:var(--ts-primary);font-size:.62rem;font-weight:800;letter-spacing:.12em}
    .uae-identity h3{margin:.1rem 0 .35rem;overflow:hidden;font-size:1.12rem;text-overflow:ellipsis;white-space:nowrap}
    .uae-badges{display:flex;flex-wrap:wrap;gap:.35rem}
    .uae-badge{padding:.2rem .55rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:999px;background:var(--ts-surface-inset,var(--ts-surface));font-size:.64rem;font-weight:750}
    .tone-success{color:var(--ts-success)!important;border-color:color-mix(in srgb,var(--ts-success) 40%,transparent)!important;background:color-mix(in srgb,var(--ts-success) 11%,transparent)!important}
    .tone-danger{color:var(--ts-danger)!important;border-color:color-mix(in srgb,var(--ts-danger) 40%,transparent)!important;background:color-mix(in srgb,var(--ts-danger) 11%,transparent)!important}
    .tone-warning{color:var(--ts-warning)!important;border-color:color-mix(in srgb,var(--ts-warning) 40%,transparent)!important;background:color-mix(in srgb,var(--ts-warning) 11%,transparent)!important}
    .uae-close{display:grid;place-items:center;width:38px;height:38px;padding:0;color:var(--ts-text-muted);border:1px solid var(--ts-border);border-radius:10px;background:var(--ts-surface);cursor:pointer}
    .uae-close:hover{color:var(--ts-text);border-color:var(--ts-primary)}
    .uae-tabs{padding:0 1.2rem}
    .uae-body{flex:1 1 auto;min-height:0;overflow:auto;padding:1.1rem 1.2rem 1.2rem;border-top:1px solid var(--ts-border-strong);background:var(--ts-surface)}
    .uae-panel{display:grid;gap:1rem}
    .uae-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.85rem}
    .uae-full{grid-column:1/-1}
    .uae-field{display:grid;gap:.35rem;min-width:0}
    .uae-field>span{color:var(--ts-text-muted);font-size:.72rem;font-weight:750}
    .uae-field input,.uae-password-input input{box-sizing:border-box;width:100%;min-height:42px;padding:.55rem .75rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-input-bg,var(--ts-bg));font:inherit;font-size:.86rem}
    .uae-field input:focus,.uae-password-input input:focus{outline:none;border-color:var(--ts-primary);box-shadow:0 0 0 3px var(--ts-focus-ring)}
    .uae-hint{color:var(--ts-text-subtle);font-size:.68rem}
    .uae-field-error{color:var(--ts-danger);font-size:.7rem;font-weight:650}
    .uae-meta{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.6rem}
    .uae-meta div{padding:.65rem .75rem;border:1px solid var(--ts-border);border-radius:11px;background:var(--ts-surface-inset,var(--ts-bg-elevated))}
    .uae-meta small{display:block;color:var(--ts-text-subtle);font-size:.64rem;font-weight:750;text-transform:uppercase;letter-spacing:.06em}
    .uae-meta strong{display:block;margin-top:.15rem;font-size:.8rem}
    .uae-businesses h4{margin:0 0 .5rem;font-size:.8rem;color:var(--ts-text-muted)}
    .uae-businesses article{display:grid;grid-template-columns:36px minmax(0,1fr) auto;align-items:center;gap:.7rem;padding:.6rem .7rem;border:1px solid var(--ts-border);border-radius:12px;background:var(--ts-bg-elevated)}
    .uae-businesses article+article{margin-top:.45rem}
    .uae-business-icon{display:grid;place-items:center;width:36px;height:36px;border-radius:10px;color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 13%,transparent)}
    .uae-businesses strong{display:block;overflow:hidden;font-size:.84rem;text-overflow:ellipsis;white-space:nowrap}
    .uae-businesses small{color:var(--ts-text-muted);font-size:.7rem}
    .uae-actions,.uae-card-actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:.55rem}
    .uae-card{display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:start;gap:.85rem;padding:1rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-elevated)}
    .uae-card>button{align-self:center}
    .uae-card-icon{display:grid;place-items:center;width:42px;height:42px;border-radius:12px;color:var(--ts-primary);border:1px solid color-mix(in srgb,var(--ts-primary) 35%,transparent);background:color-mix(in srgb,var(--ts-primary) 12%,transparent);font-size:1.1rem}
    .uae-card-copy{display:grid;gap:.45rem;min-width:0}
    .uae-card-copy h4{margin:0;font-size:.9rem}
    .uae-card-copy p{margin:0;color:var(--ts-text-muted);font-size:.76rem;line-height:1.5;overflow-wrap:anywhere}
    .uae-card-copy p strong{color:var(--ts-text)}
    .uae-card:has(.uae-password-row){grid-template-columns:42px minmax(0,1fr)}
    .uae-password-row{display:flex;flex-wrap:wrap;gap:.45rem;margin-top:.2rem}
    .uae-password-input{position:relative;flex:1 1 220px;min-width:0}
    .uae-password-input input{padding-right:2.6rem;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;letter-spacing:.02em}
    .uae-password-input button{position:absolute;top:0;bottom:0;right:.35rem;display:grid;place-items:center;width:32px;height:32px;margin:auto 0;padding:0;color:var(--ts-text-muted);border:0;border-radius:8px;background:transparent;cursor:pointer}
    .uae-check{display:flex;align-items:flex-start;gap:.5rem;color:var(--ts-text);font-size:.76rem;line-height:1.45;cursor:pointer}
    .uae-check input{flex:0 0 auto;width:16px;height:16px;margin:.1rem 0 0;accent-color:var(--ts-primary)}
    .uae-self-note{display:flex;gap:.5rem;margin:0;padding:.7rem .85rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-elevated);font-size:.76rem;line-height:1.5}
    .uae-self-note i{color:var(--ts-info,var(--ts-primary))}
    .uae-primary,.uae-secondary,.uae-danger{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-height:40px;padding:0 1rem;border-radius:10px;font:inherit;font-size:.8rem;font-weight:750;white-space:nowrap;cursor:pointer;transition:filter .15s ease,border-color .15s ease,background .15s ease}
    .uae-primary{color:var(--ts-on-primary,#fff);border:0;background:linear-gradient(135deg,var(--ts-primary),var(--ts-primary-strong,var(--ts-primary)))}
    .uae-secondary{color:var(--ts-text);border:1px solid var(--ts-border-strong);background:var(--ts-surface)}
    .uae-secondary:hover:not(:disabled){border-color:var(--ts-primary);background:var(--ts-surface-hover)}
    .uae-danger{color:var(--ts-danger);border:1px solid color-mix(in srgb,var(--ts-danger) 45%,transparent);background:color-mix(in srgb,var(--ts-danger) 10%,transparent)}
    .uae-primary:hover:not(:disabled),.uae-danger:hover:not(:disabled){filter:brightness(1.08)}
    .uae-primary:disabled,.uae-secondary:disabled,.uae-danger:disabled{opacity:.55;cursor:not-allowed}
    .uae-compact{min-height:42px;padding:0 .8rem}
    button:focus-visible{outline:3px solid var(--ts-focus-ring);outline-offset:2px}
    .uae-loading,.uae-error{display:flex;align-items:center;justify-content:center;gap:.6rem;padding:2rem 1rem;color:var(--ts-text-muted);font-size:.82rem}
    .uae-error{flex-direction:column;color:var(--ts-danger);text-align:center}
    .uae-spinner{width:22px;height:22px;border:3px solid var(--ts-border-strong);border-top-color:var(--ts-primary);border-radius:50%;animation:uaeSpin .8s linear infinite}
    @keyframes uaeRise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
    @keyframes uaeSpin{to{transform:rotate(360deg)}}
    @media(max-width:620px){
      .uae-overlay{place-items:end center;padding:0}
      .uae-modal{width:100%;max-height:94dvh;border-radius:18px 18px 0 0}
      .uae-head{grid-template-columns:42px minmax(0,1fr) 36px;padding:.95rem .9rem .7rem}
      .uae-avatar{width:42px;height:42px;font-size:1.05rem}
      .uae-tabs{padding:0 .6rem}
      .uae-body{padding:.9rem}
      .uae-grid,.uae-meta{grid-template-columns:1fr}
      .uae-card{grid-template-columns:38px minmax(0,1fr)}
      .uae-card>button{grid-column:1/-1;width:100%}
      .uae-card-icon{width:38px;height:38px}
      .uae-actions .uae-primary,.uae-actions .uae-secondary,.uae-card-actions .uae-primary{flex:1 1 auto}
      .uae-businesses article{grid-template-columns:36px minmax(0,1fr)}
      .uae-businesses article button{grid-column:1/-1}
    }
    @media(prefers-reduced-motion:reduce){.uae-modal{animation:none}}
  `],
})
export class UserAccountEditorComponent implements OnInit {
  @Input({ required: true }) userId!: number;
  @Input() initialTab: EditorTab = 'datos';
  @Output() closed = new EventEmitter<void>();
  /** La cuenta cambió (datos, estado o contraseña): refrescar listados. */
  @Output() changed = new EventEmitter<AdminUserAccount>();
  @Output() openBusiness = new EventEmitter<{ id: number; name: string }>();

  private readonly subscriptions = inject(SubscriptionService);
  private readonly auth = inject(AuthService);
  private readonly dialogs = inject(DialogService);
  private readonly notifications = inject(NotificationService);

  readonly tabs: AdminTabOption[] = [
    { id: 'datos', label: 'Datos', icon: 'bi-person-vcard' },
    { id: 'contrasena', label: 'Contraseña', icon: 'bi-key' },
    { id: 'acceso', label: 'Acceso y sesiones', icon: 'bi-shield-lock' },
  ];

  readonly tab = signal<EditorTab>('datos');
  readonly loading = signal(true);
  readonly loadError = signal('');
  readonly user = signal<AdminUserAccount | null>(null);
  readonly businesses = signal<AdminUserBusiness[]>([]);
  readonly saving = signal(false);
  readonly submitted = signal(false);
  readonly sendingLink = signal(false);
  readonly savingPassword = signal(false);
  readonly passwordTouched = signal(false);
  readonly showPassword = signal(false);
  readonly togglingStatus = signal(false);
  readonly revoking = signal(false);

  form = { name: '', email: '', phone_number: '' };
  newPassword = '';
  forceChange = true;
  notifyUser = true;

  readonly initial = computed(() => (this.user()?.name || this.user()?.email || 'U').trim().charAt(0).toUpperCase());
  readonly isSelf = computed(() => Number(this.auth.currentUser()?.session_user?.user_id) === Number(this.user()?.user_id));

  ngOnInit(): void {
    this.tab.set(this.initialTab);
    this.load();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!document.querySelector('.dialog-overlay')) this.close();
  }

  close(): void { this.closed.emit(); }
  selectTab(id: string): void { this.tab.set(id as EditorTab); }
  roleLabel(role: string): string { return ROLE_LABELS[role] || role; }
  formatDate(value: string | null | undefined, withTime = false): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-SV', withTime
      ? { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }
  relationLabel(relation: string): string { return RELATION_LABELS[relation] || relation; }

  load(): void {
    this.loading.set(true);
    this.loadError.set('');
    this.subscriptions.getAdminUser(this.userId).subscribe({
      next: ({ user, businesses }) => {
        this.user.set(user);
        this.businesses.set(businesses || []);
        this.resetForm();
        this.loading.set(false);
      },
      error: error => {
        this.loadError.set(friendlyError(error, 'No fue posible cargar la cuenta.'));
        this.loading.set(false);
      },
    });
  }

  resetForm(): void {
    const u = this.user();
    this.form = { name: u?.name || '', email: u?.email || '', phone_number: u?.phone_number || '' };
    this.submitted.set(false);
  }

  private normalizedForm() {
    return {
      name: this.form.name.replace(/\s+/g, ' ').trim(),
      email: this.form.email.trim().toLowerCase(),
      phone_number: this.form.phone_number.replace(/[\s\-().]/g, ''),
    };
  }

  profileDirty(): boolean {
    const u = this.user();
    if (!u) return false;
    const f = this.normalizedForm();
    return f.name !== (u.name || '') || f.email !== String(u.email || '').toLowerCase() || f.phone_number !== String(u.phone_number || '').replace(/[\s\-().]/g, '');
  }

  nameError(): string {
    const name = this.normalizedForm().name;
    return name.length < 2 ? 'Ingresa al menos 2 caracteres.' : '';
  }
  emailError(): string {
    return EMAIL_RE.test(this.normalizedForm().email) ? '' : 'Ingresa un correo electrónico válido.';
  }
  phoneError(): string {
    const phone = this.normalizedForm().phone_number;
    return phone && !PHONE_RE.test(phone) ? 'Usa entre 7 y 15 dígitos; solo se admite un + al inicio.' : '';
  }

  async saveProfile(): Promise<void> {
    const u = this.user();
    if (!u || this.saving()) return;
    this.submitted.set(true);
    if (this.nameError() || this.emailError() || this.phoneError() || !this.profileDirty()) return;
    const payload = this.normalizedForm();
    const emailChanged = payload.email !== String(u.email || '').toLowerCase();
    if (emailChanged) {
      // El correo es la credencial de acceso: se confirma antes de cambiarlo.
      const confirmed = await this.dialogs.confirm({
        title: 'Cambiar correo de acceso',
        message: `${u.name} pasará a iniciar sesión con ${payload.email} en lugar de ${u.email}. Se enviará un aviso a ambos correos.`,
        tone: 'warning',
        confirmText: 'Cambiar correo',
      });
      if (!confirmed) return;
    }
    this.saving.set(true);
    try {
      const response = await firstValueFrom(this.subscriptions.updateAdminUser(u.user_id, payload));
      this.user.set(response.user);
      this.resetForm();
      this.notifications.success(response.msg || 'Datos actualizados.');
      this.changed.emit(response.user);
    } catch (error) {
      this.notifications.error(friendlyError(error, 'No fue posible actualizar la cuenta.'));
    } finally {
      this.saving.set(false);
    }
  }

  async sendRecoveryLink(): Promise<void> {
    const u = this.user();
    if (!u || this.sendingLink()) return;
    this.sendingLink.set(true);
    try {
      await this.dialogs.confirmAndRun(
        {
          title: 'Enviar recuperación de contraseña',
          message: `TuSala enviará un enlace seguro y de uso temporal a ${u.email}.\n\nLa contraseña actual no se envía ni se muestra: ${u.name} definirá una nueva mediante el enlace.`,
          tone: 'warning',
          confirmText: 'Enviar enlace',
        },
        () => this.subscriptions.resetUserPassword(u.user_id, currentFrontendBaseUrl()),
        'Enlace de recuperación enviado correctamente.',
        (error: unknown) => friendlyError(error, 'No se pudo enviar el enlace de recuperación.'),
      );
    } catch { /* el modal de resultado ya muestra el error */ } finally {
      this.sendingLink.set(false);
    }
  }

  generatePassword(): void {
    const values = new Uint32Array(12);
    crypto.getRandomValues(values);
    this.newPassword = Array.from(values, v => PASSWORD_ALPHABET[v % PASSWORD_ALPHABET.length]).join('');
    this.showPassword.set(true);
    this.passwordTouched.set(false);
  }

  async copyPassword(): Promise<void> {
    if (!this.newPassword) return;
    try {
      await navigator.clipboard.writeText(this.newPassword);
      this.notifications.success('Contraseña copiada al portapapeles.');
    } catch {
      this.notifications.error('No se pudo copiar. Selecciónala y cópiala manualmente.');
    }
  }

  async savePassword(): Promise<void> {
    const u = this.user();
    if (!u || this.savingPassword() || this.isSelf()) return;
    this.passwordTouched.set(true);
    if (this.newPassword.length < 6) return;
    const password = this.newPassword;
    this.savingPassword.set(true);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Asignar nueva contraseña',
          message: `Se reemplazará la contraseña de ${u.name} y se cerrarán todas sus sesiones.${this.forceChange ? ' Deberá crear una nueva al iniciar sesión.' : ''}\n\nComparte la contraseña solo por un canal seguro.`,
          tone: 'warning',
          confirmText: 'Asignar contraseña',
        },
        () => this.subscriptions.setAdminUserPassword(u.user_id, { password, force_change: this.forceChange, notify: this.notifyUser }),
        res => res.email_warning ? { tone: 'info', message: res.msg } : res.msg,
        (error: unknown) => friendlyError(error, 'No fue posible actualizar la contraseña.'),
      );
      if (result) {
        this.user.set({ ...u, force_password_change: this.forceChange });
        this.newPassword = '';
        this.passwordTouched.set(false);
        this.showPassword.set(false);
        this.changed.emit(this.user()!);
      }
    } catch { /* el modal de resultado ya muestra el error */ } finally {
      this.savingPassword.set(false);
    }
  }

  async toggleStatus(): Promise<void> {
    const u = this.user();
    if (!u || this.togglingStatus() || this.isSelf()) return;
    const activate = !u.is_active;
    this.togglingStatus.set(true);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: activate ? 'Activar acceso' : 'Desactivar acceso',
          message: activate
            ? `${u.name} podrá volver a iniciar sesión.`
            : `${u.name} no podrá iniciar sesión hasta que se reactive su acceso. Sus datos no se eliminan.`,
          tone: activate ? 'info' : 'danger',
          danger: !activate,
          confirmText: activate ? 'Activar acceso' : 'Desactivar acceso',
        },
        () => this.subscriptions.updateUserStatus(u.user_id, activate),
        activate ? 'Acceso activado.' : 'Acceso desactivado.',
        (error: unknown) => friendlyError(error, 'No fue posible actualizar el acceso.'),
      );
      if (result) {
        this.user.set({ ...u, is_active: activate });
        this.changed.emit(this.user()!);
      }
    } catch { /* el modal de resultado ya muestra el error */ } finally {
      this.togglingStatus.set(false);
    }
  }

  async revokeSessions(): Promise<void> {
    const u = this.user();
    if (!u || this.revoking() || this.isSelf()) return;
    this.revoking.set(true);
    try {
      await this.dialogs.confirmAndRun(
        {
          title: 'Cerrar todas las sesiones',
          message: `${u.name} saldrá de TuSala en todos sus dispositivos y deberá iniciar sesión nuevamente.`,
          tone: 'warning',
          confirmText: 'Cerrar sesiones',
        },
        () => this.subscriptions.revokeAdminUserSessions(u.user_id),
        'Se cerraron todas las sesiones del usuario.',
        (error: unknown) => friendlyError(error, 'No fue posible cerrar las sesiones.'),
      );
    } catch { /* el modal de resultado ya muestra el error */ } finally {
      this.revoking.set(false);
    }
  }
}
